import { ApolloCache } from "@apollo/client"
import { stripTypename } from "@apollo/client/utilities"
import { cloneDeep } from "lodash-es"

import { MutationTypename } from "shared/api/types"
import {
  ProductPositionTypename,
  ProjectParentTypename,
  ProjectTypename,
  RoomTypename
} from "shared/types/Project"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"

import { getChildPositionData } from "entities/project-children/lib/getChildMoveData"

import { getProductPositionComplete } from "../lib/getProductPositionComplete"
import { CreateProductPositionParams } from "../types/ProductPosition"

export function prepareProductPositionCreate({
  projectID,
  parentID,
  displayPosition,
  productPosition,
  addons,
  cache
}: {
  projectID: string
  parentID?: string
  displayPosition?: number
  productPosition: CreateProductPositionParams
  addons: ProductPositionAddon[]
  cache: ApolloCache<object>
}) {
  const positionData = getChildPositionData({
    childID: productPosition.id,
    childType: ProductPositionTypename,
    projectID,
    displayPosition,
    parentID,
    cache
  })

  const resolvedPosition =
    positionData.nextPosition !== -1 ? positionData.nextPosition : undefined

  const input: CreateProductPositionParams & { position?: number } = {
    ...cloneDeep(productPosition),
    label: productPosition.variantKey,
    dimensioning: productPosition.dimensioning
      ? stripTypename(productPosition.dimensioning)
      : productPosition.dimensioning,
    position: resolvedPosition,
    price: productPosition.price === null ? undefined : productPosition.price
  }

  const optimisticProductPosition = getProductPositionComplete({
    productPosition: {
      __typename: ProductPositionTypename,
      ...productPosition,
      addons,
      position: resolvedPosition ?? 0,
      priceGroup: null,
      price: null,
      dimensioning: null // remove dimensioning from optimistic response to avoid cache issues
    }
  })

  const parentType: ProjectParentTypename =
    parentID === projectID || !parentID ? ProjectTypename : RoomTypename

  const optimisticResponse = optimisticProductPosition
    ? {
        __typename: MutationTypename,
        productPosition: optimisticProductPosition
      }
    : undefined

  return {
    input,
    optimisticResponse,
    parentType
  }
}
